import { useEffect, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Feature, Geometry } from "geojson";
import { useMapContext } from "@/hooks/useMap";
import {
  DEFAULT_TASK_LAYER_COLOR,
  TASK_LAYER_KEY,
  TASK_SOURCE_ID,
  TASK_POINT_LAYER_ID,
  TASK_LINE_LAYER_ID,
  TASK_POLYGON_LAYER_ID,
  TASK_LAYER_IDS,
} from "@/constant/layers";
import { LayerService } from "@/services/layerService";
import { useTenant } from "@/hooks/useTenant";
import { useTaskContext } from "@/context/taskContext";
import type { TaskGeometry } from "@/types/task";

const DEFAULT_COLOR = DEFAULT_TASK_LAYER_COLOR;

// EMPTY_FEATURE_COLLECTION removed - not needed for declarative Source

type TaskFeatureProperties = {
  taskId: string;
  title: string;
  strokeColor: string;
  fillColor: string;
};

const normalizeGeometry = (
  geometry?: TaskGeometry | string | null
): TaskGeometry | null => {
  if (!geometry) return null;
  if (typeof geometry === "string") {
    try {
      return JSON.parse(geometry) as TaskGeometry;
    } catch (error) {
      console.warn("TaskLayer: Failed to parse geometry string", error);
      return null;
    }
  }
  return geometry;
};

const createFeatureFromTask = (
  task: {
    id: string;
    title?: string | null;
    status?: { color?: string } | null;
    category?: { color?: string } | null;
    geom?: TaskGeometry | string | null;
  },
  color: string
): Feature<Geometry, TaskFeatureProperties> | null => {
  const geometry = normalizeGeometry(
    (task.geom ?? null) as TaskGeometry | string | null
  );
  if (!geometry) {
    return null;
  }

  return {
    type: "Feature",
    id: task.id,
    geometry: geometry as Geometry,
    properties: {
      taskId: task.id,
      title: task.title ?? "Task",
      strokeColor: color,
      fillColor: color,
    },
  };
};

// Note: onTaskLocationClick prop kept for backward compatibility but is no longer used.
// Click handling for task features is now managed by AddLayersManager
// which aggregates features from ALL layers into a unified popup.
interface TaskLayerProps {
  onTaskLocationClick?: (
    lat: number,
    lng: number,
    screenX: number,
    screenY: number
  ) => void;
}

export const TaskLayer: React.FC<TaskLayerProps> = () => {
  const { mapInstance, taskLayerVisible } = useMapContext();
  const { tasks, loading: tasksLoading } = useTaskContext();
  const { tenantId } = useTenant();

  // Subscribe to layers to react to admin order changes
  const { data: allLayers = [] } = useQuery({
    queryKey: ["asset-layers", tenantId],
    queryFn: () => LayerService.getAllLayers(tenantId!),
    enabled: !!tenantId,
  });
  const taskLayerRow = (
    allLayers as Array<{
      key: string;
      display_order?: number;
      color?: string | null;
    }>
  ).find((l) => l.key === TASK_LAYER_KEY);
  const taskDisplayOrder: number | undefined =
    typeof taskLayerRow?.display_order === "number"
      ? (taskLayerRow.display_order as number)
      : undefined;
  const taskLayerColor = taskLayerRow?.color || DEFAULT_TASK_LAYER_COLOR;

  // Convert tasks to GeoJSON feature collection
  const featureCollection = useMemo(() => {
    const features = tasks
      .map((task) => {
        const feature = createFeatureFromTask(task, taskLayerColor);
        if (!feature && task.geom) {
          // Invalid geometry for task; skip feature
        }
        return feature;
      })
      .filter((f): f is Feature<Geometry, TaskFeatureProperties> => f !== null);

    return {
      type: "FeatureCollection" as const,
      features,
    };
  }, [tasks, taskLayerColor]);

  // Note: Click handling for task features is now managed by AddLayersManager
  // which aggregates features from ALL layers (tasks, ArcGIS, dynamic) into a unified popup.
  // This component only handles layer rendering and visibility.

  // Trigger unified layer ordering (handled by AddLayersManager)
  // This simplified version just notifies that task layers need repositioning
  const positionTaskLayers = useCallback(() => {
    try {
      // Emit event to trigger unified layer ordering in AddLayersManager
      // AddLayersManager now handles ALL layer ordering including task layers
      window.dispatchEvent(new CustomEvent("task-layers-repositioned"));
    } catch (error) {
      console.debug("TaskLayer: Error in positionTaskLayers:", error);
    }
  }, []);

  // Note: Click handler was removed - AddLayersManager now handles all layer clicks
  // via the unified popup system

  // Position task layers after features are rendered
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || tasksLoading) return;

    if (featureCollection.features.length > 0 && taskLayerVisible) {
      setTimeout(() => positionTaskLayers(), 100);
    }
  }, [
    featureCollection,
    tasksLoading,
    mapInstance,
    taskLayerVisible,
    positionTaskLayers,
  ]);

  // Toggle visibility when requested via UI
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    const visibility = taskLayerVisible ? "visible" : "none";

    // Set visibility for all task layers
    TASK_LAYER_IDS.forEach((layerId) => {
      try {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(layerId, "visibility", visibility);
        } else {
          console.debug(`[TaskLayer] Layer ${layerId} not found on map yet`);
        }
      } catch (error) {
        console.warn(
          `[TaskLayer] Failed to set visibility for ${layerId}:`,
          error
        );
      }
    });

    if (taskLayerVisible && featureCollection.features.length > 0) {
      try {
        positionTaskLayers();
      } catch (error) {
        console.warn("TaskLayer: Failed to position layers:", error);
      }
    }
  }, [mapInstance, taskLayerVisible, positionTaskLayers, featureCollection]);

  // Handle display order changes
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    // debug: display order changed, repositioning layers
    positionTaskLayers();
  }, [mapInstance, positionTaskLayers, taskDisplayOrder]);

  // Listen for ArcGIS layer reordering events and reposition task layers
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    const handleArcgisReorder = () => {
      // Delay to let ArcGIS layers settle and be fully added to the map
      setTimeout(() => {
        positionTaskLayers();
      }, 200);
    };

    window.addEventListener("arcgis-layers-reordered", handleArcgisReorder);

    return () => {
      window.removeEventListener(
        "arcgis-layers-reordered",
        handleArcgisReorder
      );
    };
  }, [mapInstance, positionTaskLayers]);

  // Note: Drawing state monitoring removed - AddLayersManager handles this now

  // Handle basemap changes - reposition task layers when basemap changes
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    let basemapChangeDebounce: number | undefined;

    const handleBasemapChange = () => {
      // Debounce multiple basemap-changed events
      if (basemapChangeDebounce !== undefined) {
        return;
      }

      basemapChangeDebounce = window.setTimeout(() => {
        basemapChangeDebounce = undefined;
      }, 500);

      let refreshAttempted = false;
      let fallbackId: number | undefined;
      let styleDataHandler: (() => void) | null = null;

      const cleanupWaiters = () => {
        if (fallbackId !== undefined) {
          window.clearTimeout(fallbackId);
          fallbackId = undefined;
        }

        if (styleDataHandler) {
          map.off("styledata", styleDataHandler);
          styleDataHandler = null;
        }
      };

      const refreshTaskLayers = () => {
        if (refreshAttempted) {
          return;
        }
        refreshAttempted = true;
        cleanupWaiters();

        try {
          // Re-add task source and layers if they were removed by style change
          if (!map.getSource(TASK_SOURCE_ID)) {
            map.addSource(TASK_SOURCE_ID, {
              type: "geojson",
              data: featureCollection,
            });
          }

          const visibility = taskLayerVisible ? "visible" : "none";

          // Re-add polygon layer if missing
          if (!map.getLayer(TASK_POLYGON_LAYER_ID)) {
            map.addLayer({
              id: TASK_POLYGON_LAYER_ID,
              type: "fill",
              source: TASK_SOURCE_ID,
              filter: [
                "match",
                ["geometry-type"],
                ["Polygon", "MultiPolygon"],
                true,
                false,
              ],
              layout: { visibility },
              paint: {
                "fill-color": ["coalesce", ["get", "fillColor"], DEFAULT_COLOR],
                "fill-opacity": 0.4,
                "fill-outline-color": [
                  "coalesce",
                  ["get", "strokeColor"],
                  DEFAULT_COLOR,
                ],
              },
            });
          }

          // Re-add line layer if missing
          if (!map.getLayer(TASK_LINE_LAYER_ID)) {
            map.addLayer({
              id: TASK_LINE_LAYER_ID,
              type: "line",
              source: TASK_SOURCE_ID,
              filter: [
                "match",
                ["geometry-type"],
                ["LineString", "MultiLineString"],
                true,
                false,
              ],
              layout: { visibility },
              paint: {
                "line-color": [
                  "coalesce",
                  ["get", "strokeColor"],
                  DEFAULT_COLOR,
                ],
                "line-width": 4,
                "line-opacity": 0.9,
              },
            });
          }

          // Re-add point layer if missing
          if (!map.getLayer(TASK_POINT_LAYER_ID)) {
            map.addLayer({
              id: TASK_POINT_LAYER_ID,
              type: "circle",
              source: TASK_SOURCE_ID,
              filter: [
                "match",
                ["geometry-type"],
                ["Point", "MultiPoint"],
                true,
                false,
              ],
              layout: { visibility },
              paint: {
                "circle-radius": 8,
                "circle-color": [
                  "coalesce",
                  ["get", "fillColor"],
                  DEFAULT_COLOR,
                ],
                "circle-stroke-color": [
                  "coalesce",
                  ["get", "strokeColor"],
                  DEFAULT_COLOR,
                ],
                "circle-stroke-width": 2,
                "circle-opacity": 0.8,
              },
            });
          }

          // Set visibility for declarative layers after basemap change
          TASK_LAYER_IDS.forEach((layerId) => {
            try {
              if (map.getLayer(layerId)) {
                map.setLayoutProperty(layerId, "visibility", visibility);
              }
            } catch {
              // Layer not ready yet
            }
          });

          positionTaskLayers();
        } catch (error) {
          console.error(
            "TaskLayer: Failed to reposition layers after basemap change:",
            error
          );
        }
      };

      const waitForStyle = () => {
        let styleLoaded = false;
        try {
          const loaded =
            typeof map.isStyleLoaded === "function" && map.isStyleLoaded();
          styleLoaded = loaded || false;
        } catch {
          // Style check failed
        }

        if (styleLoaded) {
          refreshTaskLayers();
          return;
        }

        styleDataHandler = () => {
          let isLoaded = false;
          try {
            const loaded =
              typeof map.isStyleLoaded === "function" && map.isStyleLoaded();
            isLoaded = loaded || false;
          } catch {
            return;
          }

          if (!isLoaded) {
            return;
          }

          refreshTaskLayers();
        };

        map.on("styledata", styleDataHandler);

        fallbackId = window.setTimeout(() => {
          refreshTaskLayers();
        }, 1500);
      };

      waitForStyle();
    };

    window.addEventListener("basemap-changed", handleBasemapChange);

    return () => {
      window.removeEventListener("basemap-changed", handleBasemapChange);
      if (basemapChangeDebounce !== undefined) {
        window.clearTimeout(basemapChangeDebounce);
      }
    };
  }, [mapInstance, positionTaskLayers, taskLayerVisible, featureCollection]);

  // Add task layers using direct MapLibre GL API
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    // debug: adding task layers to map

    try {
      // Add source if it doesn't exist
      if (!map.getSource(TASK_SOURCE_ID)) {
        map.addSource(TASK_SOURCE_ID, {
          type: "geojson",
          data: featureCollection,
        });
        // debug: added task source
      } else {
        // Update existing source
        const source = map.getSource(
          TASK_SOURCE_ID
        ) as maplibregl.GeoJSONSource;
        if (source && source.setData) {
          source.setData(featureCollection);
          // debug: updated task source data
        }
      }

      // Add polygon layer if it doesn't exist
      if (!map.getLayer(TASK_POLYGON_LAYER_ID)) {
        map.addLayer({
          id: TASK_POLYGON_LAYER_ID,
          type: "fill",
          source: TASK_SOURCE_ID,
          filter: [
            "match",
            ["geometry-type"],
            ["Polygon", "MultiPolygon"],
            true,
            false,
          ],
          layout: {
            visibility: taskLayerVisible ? "visible" : "none",
          },
          paint: {
            "fill-color": ["coalesce", ["get", "fillColor"], DEFAULT_COLOR],
            "fill-opacity": 0.4,
            "fill-outline-color": [
              "coalesce",
              ["get", "strokeColor"],
              DEFAULT_COLOR,
            ],
          },
        });
        // debug: added polygon layer
      }

      // Add line layer if it doesn't exist
      if (!map.getLayer(TASK_LINE_LAYER_ID)) {
        map.addLayer({
          id: TASK_LINE_LAYER_ID,
          type: "line",
          source: TASK_SOURCE_ID,
          filter: [
            "match",
            ["geometry-type"],
            ["LineString", "MultiLineString"],
            true,
            false,
          ],
          layout: {
            visibility: taskLayerVisible ? "visible" : "none",
          },
          paint: {
            "line-color": ["coalesce", ["get", "strokeColor"], DEFAULT_COLOR],
            "line-width": 4,
            "line-opacity": 0.9,
          },
        });
        // debug: added line layer
      }

      // Add point layer if it doesn't exist
      if (!map.getLayer(TASK_POINT_LAYER_ID)) {
        map.addLayer({
          id: TASK_POINT_LAYER_ID,
          type: "circle",
          source: TASK_SOURCE_ID,
          filter: [
            "match",
            ["geometry-type"],
            ["Point", "MultiPoint"],
            true,
            false,
          ],
          layout: {
            visibility: taskLayerVisible ? "visible" : "none",
          },
          paint: {
            "circle-radius": 8,
            "circle-color": ["coalesce", ["get", "fillColor"], DEFAULT_COLOR],
            "circle-stroke-color": [
              "coalesce",
              ["get", "strokeColor"],
              DEFAULT_COLOR,
            ],
            "circle-stroke-width": 2,
            "circle-opacity": 0.8,
          },
        });
        // debug: added point layer
      }

      // (no debug test source/layer)
    } catch (error) {
      console.error("[TaskLayer ERROR] Failed to add layers:", error);
    }

    // Cleanup function to remove layers when component unmounts
    return () => {
      try {
        const currentMap = mapInstance.current;
        if (!currentMap) return;
        // Remove layers in reverse order
        if (currentMap.getLayer(TASK_POINT_LAYER_ID)) {
          currentMap.removeLayer(TASK_POINT_LAYER_ID);
        }
        if (currentMap.getLayer(TASK_LINE_LAYER_ID)) {
          currentMap.removeLayer(TASK_LINE_LAYER_ID);
        }
        if (currentMap.getLayer(TASK_POLYGON_LAYER_ID)) {
          currentMap.removeLayer(TASK_POLYGON_LAYER_ID);
        }
        if (currentMap.getSource(TASK_SOURCE_ID)) {
          currentMap.removeSource(TASK_SOURCE_ID);
        }
        // debug: cleaned up layers and sources
      } catch (error) {
        // debug: error during cleanup
        console.error("TaskLayer: Error during cleanup:", error);
      }
    };
  }, [mapInstance, featureCollection, taskLayerVisible]);

  return null; // No JSX needed - layers managed imperatively
};
